import type { Router } from 'vue-router'
import type { SessionAttributes, SessionUser } from '~/models/User/User'
import type { SessionUserInfo } from '~/models/User/SessionUserInfo'
import type { ActiveUserFilter } from '~/models/User/ActiveUserFilter'
import type { SubjectCode } from '~/models/Subject'
import type { Organization } from '~/models/Organization'
import type { GradeCode } from '~/models/Grade'
import { computed, ref, watch } from 'vue'
import { defineStore, storeToRefs } from 'pinia'
import axios from 'axios'
import { useRouter } from '#imports'
import { isExcludedSubject, sortBySubjectIndex } from '~/utils/subjectSorter'
import { findGradesBySubject, findSubjectsByGrade } from '~/utils/subjectMetadata'
import { findGradesByGradeType, findRelevantGradeType, gradesSorted, sortByGradeIndex } from '~/utils/gradeSorter'
import arrayUtils from '~/utils/arrayUtils'
import useProductStore from '~/stores/product'
import useFilterStore from '~/stores/filter'
import { UserRole } from '~/models/User/UserRole'
import { PreferredLanguage } from '~/models/User/PreferredLanguage'
import { GradeType } from '~/models/GradeType'
import usePendo from '~/composables/usePendo'
import useLanguageMap from '~/composables/useLanguageMap'
import useUserApi from '~/api/userApi'

export const useAuthStore = defineStore('auth', () => {
  const { getUser, postUser, postUserOrganization, getSessionAttributes, invalidateLegacyUser } = useUserApi()
  const product = storeToRefs(useProductStore())
  const { intersect, unique } = arrayUtils()
  const { initPendo, updatePendo } = usePendo()
  const { languageMap } = useLanguageMap()

  const user = ref<SessionUser>()
  const session = ref<SessionAttributes>()

  const isLoading = ref(false)
  const hasLoadedUser = ref(false)

  const userId = computed(() => user.value?.userId)
  const userRole = computed(() => user.value?.role[0] ?? UserRole.Student)

  const isAuthenticated = computed(() => !!userId.value)

  const gradeCodes = computed(() => (user.value?.grades || []).map(grade => grade.code).filter(code => gradesSorted.includes(code)))
  const hasGrades = computed(() => gradeCodes.value.filter(g => gradesSorted.includes(g)).length > 0)
  const userGradesWithoutReadOnly = computed((): GradeCode[] => gradeCodes.value.filter(grade => !userReadOnlyGrades.value.includes(grade)))

  const userGrades = computed((): GradeCode[] => {
    if (intersect(userReadOnlyGrades.value, selectedGradeTypeGrades.value).length === 0)
      return userGradesWithoutReadOnly.value
    return [...gradeCodes.value].sort(sortByGradeIndex)
  })

  const userReadOnlyGrades = computed((): GradeCode[] => (user.value?.grades || [])
    .filter(g => g.source === 'FEIDE')
    .map(g => g.code))

  const subjectCodes = computed(() => (user.value?.subjects || []).map(s => s.code))
  const hasSubjects = computed(() => subjectCodes.value.length > 0)
  const userSubjects = computed(() => subjectCodes.value.filter(unique<SubjectCode>).sort(sortBySubjectIndex))

  const userOrganization = computed(() => {
    if (session.value?.activeOrganization) {
      return {
        name: session.value?.activeOrganizationName ?? '',
        number: session.value.activeOrganization,
      }
    }
    return userOrganizations.value.length === 1
      ? userOrganizations.value[0]
      : undefined
  })

  const hasOrganization = computed(() => !!userOrganization.value?.number)
  const userOrganizations = computed((): Organization[] => user.value?.schoolOrganizations ?? [])
  const hasMultipleOrganizations = computed(() => userOrganizations.value.length > 1)
  const needsOrganization = computed(() => !hasOrganization.value && hasMultipleOrganizations.value)

  const hasPreferredLanguage = computed(() => !!user.value?.preferredLanguage)
  const userPreferredLanguage = computed(() => user.value?.preferredLanguage || PreferredLanguage.Bokmal)
  const userPreferredGrade = computed(() => user.value?.preferredGrade)
  const userPreferredLanguageCode = computed(() => (languageMap[userPreferredLanguage.value]))

  const isTeacher = computed(() => userRole.value === UserRole.Teacher)
  const isStudent = computed(() => userRole.value === UserRole.Student)
  const activeUserFilters = computed((): ActiveUserFilter[] => user.value?.activeUserFilters || [])

  /**
   * Finds subjects that matches users' grades
   */
  const userSubjectsByGrades = computed((): SubjectCode[] => {
    return userGrades.value
      .flatMap(findSubjectsByGrade)
      .filter(unique)
      .filter(subjectCode => !isExcludedSubject(subjectCode))
      .sort(sortBySubjectIndex)
  })

  /**
   * Finds grades that matchers users' subjects
   */
  const userGradesBySubjects = computed((): GradeCode[] => {
    return userSubjectsByGrades.value
      .flatMap(findGradesBySubject)
      .filter(unique)
      .sort(sortByGradeIndex)
  })

  /**
   * Finds grades that matchers users' grade type
   */
  const userGradesByGradeType = computed((): GradeCode[] => {
    return selectedGradeType.value
      ? findGradesByGradeType(selectedGradeType.value)
      : []
  })

  /**
   * Finds relevant grades which is either
   *
   *  1. Grades in their settings
   *  2. Grades matching subjects
   *  3. No grades
   */
  const userRelevantGrades = computed((): GradeCode[] => {
    if (userGrades.value.length)
      return userGrades.value
    if (userGradesBySubjects.value.length)
      return userGradesBySubjects.value
    return []
  })

  /**
   * The selected grade type is the grade type that is most relevant to the user. For most students
   * this will be the readOnlyGrade. If a user has multiple grades, we will select the grade type
   * after these rules:
   * 1. If there is only one grade, we select that grade type
   * 2. If all user grades belong to the same grade type, we select that grade type
   * 3. If the user has multiple grades, we select the grade type with the most grades
   *    a: readOnlyGrades only count as half a grade, in order to prioritize the other selected
   *       grade higher than the readOnlyGrade, as the readOnlyGrade can not be changed.
   * 4. If the user has multiple grades, but no grade type has higher score than the others grades,
   *    we select the lowest grade type of the highest ranked grade types.
   */
  const selectedGradeType = computed((): GradeType | undefined => {
    if (!user.value || gradeCodes.value.length === 0)
      return undefined
    if (gradeCodes.value.length === 1)
      return findRelevantGradeType(gradeCodes.value[0])

    const allUserGradesBelongToSameGradeType = gradeCodes.value.map(findRelevantGradeType).filter(unique).length === 1
    if (allUserGradesBelongToSameGradeType)
      return findRelevantGradeType(gradeCodes.value[0])

    const gradeTypeCounts = gradeCodes.value.reduce((gradeTypes, grade) => {
      const gradeType = findRelevantGradeType(grade)
      if (!gradeType)
        return gradeTypes
      if (!gradeTypes[gradeType])
        gradeTypes[gradeType] = 0
      if (isReadOnlyGrade(grade))
        gradeTypes[gradeType] += 0.25
      else gradeTypes[gradeType] += 1
      return gradeTypes
    }, {} as Record<GradeType, number>)

    const gradeTypesSortedByGradeWeighting = Object.entries(gradeTypeCounts)
      .sort((gradeTypeCountA, gradeTypeCountB) => {
        if (gradeTypeCountA[1] === gradeTypeCountB[1])
          return 0
        return gradeTypeCountA[1] > gradeTypeCountB[1] ? -1 : 1
      })

    if (gradeTypesSortedByGradeWeighting[0][0] && gradeTypesSortedByGradeWeighting[0][0] > gradeTypesSortedByGradeWeighting[1][0]) {
      return gradeTypesSortedByGradeWeighting[0][0] as GradeType
    }

    const possibleGradeTypes = gradeTypesSortedByGradeWeighting
      .filter(([, count]) => count === gradeTypesSortedByGradeWeighting[0][1])
      .map(([gradeType]) => gradeType as GradeType)
      // sort the gradeTypes based on the position in the GradeType enum:
      .sort((a, b) => Object.values(GradeType).indexOf(a) - Object.values(GradeType).indexOf(b))

    return possibleGradeTypes[0]
  })

  const selectedGradeTypeGrades = computed(() => selectedGradeType.value
    ? findGradesByGradeType(selectedGradeType.value)
    : [])

  /**
   * The way we set gradeType is by simply selecting all grades in the gradeType. For upperSecondary
   * we also added the possibility to limit the grades to fewer than all of them.
   */
  function setGradeType(type: GradeType, limitToGrades?: GradeCode[]) {
    if (!user.value)
      throw new Error('Missing user')

    const gradeTypeGrades = findGradesByGradeType(type).sort(sortByGradeIndex)

    limitToGrades = limitToGrades?.filter(grade => gradeTypeGrades.includes(grade))
      .sort(sortByGradeIndex)

    user.value.grades = (limitToGrades?.length ? limitToGrades : gradeTypeGrades).filter(unique).map(code => ({ code, source: 'LOCAL' }))

    let preferredGrade = gradeTypeGrades[0]

    // Students who change grade type, should have their Feide grade as preferred grade
    // in order to avoid confusion
    if (intersect(userReadOnlyGrades.value, gradeTypeGrades).length > 0) {
      preferredGrade = userReadOnlyGrades.value[0]
    } else {
      // at this point, preferredGrade is either not set or is a grade outside of the current
      // gradeType, so let's set it to the first grade in the current grade type instead
      preferredGrade = limitToGrades?.length ? limitToGrades[0] : preferredGrade
    }

    setPreferredGrade(preferredGrade)
  }

  const buildSessionUser = (user: SessionUser): SessionUser => ({ ...user, ...{ hash: session.value?.gaHash } })

  /**
   * SessionUserInfo is the user object accepted by the API
   */
  const sessionUserInfo = computed((): SessionUserInfo => ({
    grades: userGrades.value.map(code => ({ code })),
    subjects: userSubjects.value.map(code => ({ code })),
    preferredLanguage: userPreferredLanguage.value,
    preferredGrade: userPreferredGrade.value,
    termsOfServiceAccepted: true,
    activeUserFilters: activeUserFilters.value.filter(filter => userOrganizations.value.some((org) => {
      return org.number === filter.organizationNumber
    })),
  }))

  async function acquireSession(router?: Router) {
    isLoading.value = true
    try {
      const response = await getUser()
      session.value = await getSessionAttributes()
      user.value = buildSessionUser(response)
      sessionInterval(router)
    } catch (error) {
      user.value = undefined
      throw error
    } finally {
      hasLoadedUser.value = true
      isLoading.value = false
    }
  }

  function sessionInterval(router = useRouter()) {
    const seconds = Number.parseInt(<string>import.meta.env.VITE_SESSION_INTERVAL)
    const session = setInterval(async () => {
      try {
        await getUser()
      } catch (error) {
        console.error(error)
        if (
          axios.isAxiosError(error)
          && error.response?.status === 401
        ) {
          clearInterval(session)
          user.value = undefined
          await router.push({
            name: 'login',
            query: { redirectUri: window.location.toString() },
          })
        }
      }
    }, seconds * 1000)
  }

  async function updateUser(sessionUser: SessionUserInfo) {
    if (!user.value)
      throw new Error('Missing user')
    isLoading.value = true
    try {
      user.value.activeUserFilters = sessionUser.activeUserFilters
      const response = await postUser(sessionUser)
      session.value = await getSessionAttributes()
      user.value = buildSessionUser(response)
    } finally {
      isLoading.value = false
      updatePendo(user.value, userOrganization.value)
      await invalidateLegacyUser()
    }
  }

  async function updateUserOrganization(organization: Organization) {
    if (!user.value)
      throw new Error('Missing user')
    isLoading.value = true
    try {
      const { number, name } = await postUserOrganization(organization)
      session.value = { activeOrganization: number, activeOrganizationName: name }
    } finally {
      isLoading.value = false
      updatePendo(user.value, userOrganization.value)
    }
  }

  function isReadOnlyGrade(grade: GradeCode) {
    return userReadOnlyGrades.value.includes(grade)
  }

  function isPreferredLanguage(preferredLanguage: PreferredLanguage) {
    return preferredLanguage === user.value?.preferredLanguage
  }

  function setPreferredLanguage(preferredLanguage: PreferredLanguage) {
    if (!user.value)
      throw new Error('Missing user')
    user.value.preferredLanguage = preferredLanguage
  }

  function setPreferredGrade(preferredGrade: GradeCode | undefined) {
    const { setSelectedGrade } = useFilterStore()
    if (!preferredGrade)
      return
    if (!user.value)
      throw new Error('Missing user')
    user.value.preferredGrade = preferredGrade

    updateUser(sessionUserInfo.value).then(() => {
      if (user.value?.preferredGrade)
        setSelectedGrade(user.value.preferredGrade)
    })
  }

  watch(hasLoadedUser, () => {
    if (user.value) {
      initPendo(user.value, userOrganization.value)
    }
  })

  watch(() => [isAuthenticated, user, product.hasOnlyTrialProducts], ([isAuthenticated, user, hasOnlyTrialProducts]) => {
    if (undefined === isAuthenticated.value)
      return
    if (undefined === user.value)
      return
    if (undefined === hasOnlyTrialProducts?.value)
      return

    updatePendo(<SessionUser>user.value, userOrganization.value, <boolean>hasOnlyTrialProducts?.value)
  }, { deep: true })

  return {
    user,
    userId,
    isLoading,
    isAuthenticated,
    hasGrades,
    hasSubjects,
    hasPreferredLanguage,
    userGrades,
    userReadOnlyGrades,
    userSubjects,
    userRole,
    userOrganization,
    userOrganizations,
    needsOrganization,
    hasMultipleOrganizations,
    userPreferredLanguage,
    userPreferredLanguageCode,
    userPreferredGrade,
    isTeacher,
    isStudent,
    hasOrganization,
    activeUserFilters,
    sessionUserInfo,
    acquireSession,
    updateUser,
    updateUserOrganization,
    isReadOnlyGrade,
    isPreferredLanguage,
    setPreferredLanguage,
    setPreferredGrade,
    userSubjectsByGrades,
    userRelevantGrades,
    userGradesBySubjects,
    userGradesByGradeType,
    selectedGradeType,
    setGradeType,
    selectedGradeTypeGrades,
    hasLoadedUser,
    gradeCodes,
  }
})

export default useAuthStore
